import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  DataTable,
  TableContainer,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectRow,
  TableBatchActions,
  TableBatchAction,
  TableToolbarAction,
  TableToolbarMenu,
  Button,
  Modal,
  TextInput,
  Pagination,
  Tag,
  Grid,
  Column,
  MultiSelect,
  InlineLoading,
  InlineNotification,
  ComboBox
} from "@carbon/react";
import { callApi } from "../../../services/apiService";
import { Add, Delete, Filter, TrashCan } from "@carbon/icons-react";

const headers = [
  { key: "attributeValue", header: "Attribute Value" },
  { key: "attributeSpec", header: "Attribute Spec" },
  { key: "attributeName", header: "Attribute Name" },
  { key: "status", header: "Status" },
  { key: "createdByUserId", header: "Created By User" },
  { key: "createdTime", header: "Created Time" },
];

export default function AttributeValue() {
  const mountedRef = useRef(true);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [newAttributeValue, setNewAttributeValue] = useState("");
  const [newAttributeName, setNewAttributeName] = useState(null);
  const [specsOptions, setSpecsOptions] = useState([]);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    attributeName: [],
    attributeSpec: []
  });
  
  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    attributeName: [],
    attributeSpec: []
  });
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Pagination and Search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const userId = 1;

  // ------------------ Search & Filter Logic ------------------
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim().toLowerCase()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Extract unique values for filters
  const extractUniqueValues = useCallback((data) => {
    const uniqueNames = Array.from(
      new Set(data.map(item => item.attributeName).filter(Boolean))
    ).map(name => ({ id: name, text: name }));
    
    const uniqueSpecs = Array.from(
      new Set(data.map(item => item.attributeSpec).filter(Boolean))
    ).map(spec => ({ id: spec, text: spec }));
    
    return {
      attributeName: uniqueNames,
      attributeSpec: uniqueSpecs
    };
  }, []);

  // Apply filters
  const applyFilters = useCallback((data) => {
    if (!data || data.length === 0) return data;

    return data.filter(row => {
      // Filter by attribute name
      if (filters.attributeName.length > 0) {
        const hasMatchingName = filters.attributeName.some(filter => 
          filter.text === row.attributeName
        );
        if (!hasMatchingName) return false;
      }

      // Filter by attribute spec
      if (filters.attributeSpec.length > 0) {
        const hasMatchingSpec = filters.attributeSpec.some(filter => 
          filter.text === row.attributeSpec
        );
        if (!hasMatchingSpec) return false;
      }

      return true;
    });
  }, [filters]);

  // Fetch Attribute Values
  const fetchAttributesValue = useCallback(async () => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError("");
    try {
      const response = await callApi("GET", "/attribute-values/");
      const data = Array.isArray(response?.data) ? response.data : [];
      
      const formattedRows = data.map((item) => ({
        id: item.id,
        attributeValue: item.attr_val_name,
        attributeSpec: item.atr_spec_name,
        attributeName: item.attr_name,
        attributeSpecId: item.atr_spec_id,
        attributeId: item.atr_id,
        status: item.status,
        createdByUserId: item.created_user,
        createdTime: item.created_at,
        updatedTimeAt: item.updated_at,
      }));
      
      if (!mountedRef.current) return;
      setRows(formattedRows);

      // Extract filter options from data
      const filterOptions = extractUniqueValues(formattedRows);
      setFilterOptions(prev => ({
        ...prev,
        attributeName: filterOptions.attributeName,
        attributeSpec: filterOptions.attributeSpec
      }));

    } catch (err) {
      console.error("fetchAttributesValue error:", err);
      if (!mountedRef.current) return;
      setError("Unable to load attribute values. Please try again.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [extractUniqueValues]);

  // Fetch Attribute Names for dropdown
  const fetchAttributeName = useCallback(async () => {
    try {
      const response = await callApi("GET", "/attributes/");
      const data = Array.isArray(response?.data) ? response.data : [];
      
      const options = data.map((attr) => ({
        id: attr.id,
        text: attr.attr_name,
      }));
      setSpecsOptions(options);
      
    } catch (error) {
      console.error("Failed to fetch attributes:", error.message);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchAttributesValue();
    fetchAttributeName();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAttributesValue, fetchAttributeName]);

  // Filter rows based on search and filters
  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Apply text search
    if (debouncedSearch) {
      filtered = filtered.filter((r) => {
        const s = debouncedSearch;
        return (
          (r.attributeValue || "").toLowerCase().includes(s) ||
          (r.attributeSpec || "").toLowerCase().includes(s) ||
          (r.attributeName || "").toLowerCase().includes(s) ||
          (r.status || "").toLowerCase().includes(s) ||
          (r.createdByUserId?.toString() || "").includes(debouncedSearch) ||
          (r.createdTime || "").toLowerCase().includes(s)
        );
      });
    }

    // Apply filters
    filtered = applyFilters(filtered);

    return filtered;
  }, [rows, debouncedSearch, applyFilters]);

  const totalItems = filteredRows.length;
  const startIndex = (page - 1) * pageSize;
  const paginatedRows = useMemo(() =>
    filteredRows.slice(startIndex, startIndex + pageSize),
    [filteredRows, startIndex, pageSize]
  );

  // Handle filter changes
  const handleFilterChange = (filterName, selectedItems) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: selectedItems
    }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      attributeName: [],
      attributeSpec: []
    });
    setPage(1);
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).reduce((count, filter) => {
    if (Array.isArray(filter)) {
      return count + filter.length;
    } else if (filter && filter !== "") {
      return count + 1;
    }
    return count;
  }, 0);

  // Add New
  const handleAddNew = () => {
    setIsEdit(false);
    setEditingRow(null);
    setNewAttributeValue("");
    setNewAttributeName(null);
    setIsModalOpen(true);
  };

  // Edit
  const handleEdit = (rowId) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setIsEdit(true);
    setEditingRow(row);
    setNewAttributeValue(row.attributeValue);

    const selectedName = specsOptions.find((opt) => opt.id === row.attributeId) || null;
    setNewAttributeName(selectedName);
    setIsModalOpen(true);
  };

  // Save (Add or Update)
  const handleSave = async (e) => {
    e.preventDefault();

    if (!newAttributeValue.trim() || !newAttributeName) {
      setError("Both Attribute Value and Attribute Name are required.");
      return;
    }

    const payload = {
      ATTRIBUTE_VALUE_NAME: newAttributeValue,
      ATTRIBUTE_ID: newAttributeName.id,
      ...(isEdit
        ? { ATTRIBUTE_VALUE_ID: editingRow.id, UPDATED_BY_USER_ID: userId }
        : { CREATED_BY_USER_ID: userId }),
    };

    try {
      if (isEdit && editingRow) {
        await callApi("PUT", "/attribute-values/", payload);
        setSuccess("Attribute value updated successfully.");
      } else {
        await callApi("POST", "/attribute-values/", payload);
        setSuccess("Attribute value added successfully.");
      }

      // Refresh list after save
      await fetchAttributesValue();
      setIsModalOpen(false);
      setIsEdit(false);
      setEditingRow(null);
      setNewAttributeValue("");
      setNewAttributeName(null);
    } catch (error) {
      console.error("Failed to save attribute value:", error.message);
      setError("Failed to save attribute value. Please try again.");
    }
  };

  // Delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await callApi("DELETE", `/attribute-values/${deleteId}`);
      await fetchAttributesValue();
      setSuccess("Attribute value deleted successfully.");
    } catch (error) {
      console.error("Failed to delete value:", error.message);
      setError("Failed to delete attribute value. Please try again.");
    } finally {
      setDeleting(false);
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  // Batch delete
  const handleBatchDelete = async (selectedRows) => {
    if (!selectedRows || selectedRows.length === 0) return;
    
    setDeleting(true);
    try {
      const deletePromises = selectedRows.map(row =>
        callApi("DELETE", `/attribute-values/${row.id}`)
      );
      await Promise.all(deletePromises);
      await fetchAttributesValue();
      setSuccess(`${selectedRows.length} attribute value(s) deleted successfully.`);
    } catch (error) {
      console.error("Failed to delete values:", error.message);
      setError("Failed to delete attribute values. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <DataTable
        rows={paginatedRows}
        headers={headers}
        isSortable
      >
        {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getBatchActionProps, selectedRows }) => {
          const batchActionProps = getBatchActionProps();
          
          return (
            <TableContainer title="Attribute Values">
              <TableToolbar>
                {selectedRows.length > 0 ? (
                  <TableBatchActions {...batchActionProps}>
                    <TableBatchAction
                      renderIcon={Delete}
                      onClick={() => handleBatchDelete(selectedRows)}
                      iconDescription="Delete selected rows"
                    >
                      Delete ({selectedRows.length})
                    </TableBatchAction>
                  </TableBatchActions>
                ) : (
                  <>
                    <TableToolbarContent>
                      <TableToolbarSearch
                        persistent
                        id="attribute-search"
                        placeholder="Filter table"
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />

                      <Button
                        kind="ghost"
                        renderIcon={Filter}
                        hasIconOnly
                        iconDescription="Toggle filters"
                        onClick={() => setShowFilters((v) => !v)}
                      >
                        {activeFilterCount > 0 && `(${activeFilterCount})`}
                      </Button>

                      <Button renderIcon={Add} onClick={handleAddNew}>
                        Add new
                      </Button>
                    </TableToolbarContent>
                  </>
                )}
              </TableToolbar>

              {/* Filter Section */}
              {showFilters && (
                <div
                  style={{
                    width: "100%",
                    padding: "1rem 1.25rem",
                    background: "#f4f4f4",
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <Grid narrow>
                    <Column lg={3} md={4} sm={4}>
                      <MultiSelect
                        id="filter-attribute-name"
                        label="Choose "
                        titleText="Attribute Name"
                        items={filterOptions.attributeName}
                        itemToString={(item) => item?.text || ""}
                        selectedItems={filters.attributeName}
                        onChange={({ selectedItems }) => handleFilterChange("attributeName", selectedItems)}
                      />
                    </Column>
                    <Column lg={3} md={4} sm={4}>
                      <MultiSelect
                        id="filter-attribute-spec"
                        label="Choose "
                        titleText="Attribute Spec"
                        items={filterOptions.attributeSpec}
                        itemToString={(item) => item?.text || ""}
                        selectedItems={filters.attributeSpec}
                        onChange={({ selectedItems }) => handleFilterChange("attributeSpec", selectedItems)}
                      />
                    </Column>
                    <Column lg={1} md={4} sm={4} style={{ display: "flex", alignItems: "flex-end" }}>
                      <Button
                        kind="secondary"
                        size="sm"
                        onClick={clearAllFilters}
                        style={{ width: "100%" }}
                      >
                        Clear
                      </Button>
                    </Column>
                  </Grid>
                  
                  {/* Active Filters Display */}
                  {activeFilterCount > 0 && (
                    <div style={{ 
                      marginTop: "1rem", 
                      display: "flex", 
                      alignItems: "center", 
                      flexWrap: "wrap", 
                      gap: "8px" 
                    }}>
                      <span style={{ fontSize: "0.875rem", fontWeight: "600", color: "#525252" }}>
                        Active Filters:
                      </span>
                      
                      {filters.attributeName.map((filter, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Tag type="blue" size="sm">Attribute: {filter.text}</Tag>
                          <Button
                            hasIconOnly
                            iconDescription="Remove filter"
                            kind="ghost"
                            size="sm"
                            onClick={() => handleFilterChange("attributeName", 
                              filters.attributeName.filter(f => f.id !== filter.id)
                            )}
                            renderIcon={TrashCan}
                          />
                        </div>
                      ))}
                      
                      {filters.attributeSpec.map((filter, index) => (
                        <div key={index} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Tag type="teal" size="sm">Spec: {filter.text}</Tag>
                          <Button
                            hasIconOnly
                            iconDescription="Remove filter"
                            kind="ghost"
                            size="sm"
                            onClick={() => handleFilterChange("attributeSpec", 
                              filters.attributeSpec.filter(f => f.id !== filter.id)
                            )}
                            renderIcon={TrashCan}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Loading and Error Messages */}
              {loading && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <InlineLoading description="Loading attribute values..." />
                </div>
              )}

              {error && !loading && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="error"
                    title="Error"
                    subtitle={error}
                    lowContrast
                    onClose={() => setError("")}
                  />
                </div>
              )}

              {success && (
                <div style={{ padding: "1rem" }}>
                  <InlineNotification
                    kind="success"
                    title="Success"
                    subtitle={success}
                    lowContrast
                    onClose={() => setSuccess("")}
                  />
                </div>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                    <th />
                    {headers.map((header) => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {!loading && paginatedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length + 2} style={{ textAlign: 'center', padding: '2rem' }}>
                        {searchTerm || activeFilterCount > 0 
                          ? 'No results found. Try adjusting your search or filters.' 
                          : 'No attribute values found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        <TableSelectRow {...getSelectionProps({ row })} />
                        <TableCell>{row.attributeValue}</TableCell>
                        <TableCell>{row.attributeSpec || "-"}</TableCell>
                        <TableCell>{row.attributeName}</TableCell>
                         
                          <TableCell> {row.status}</TableCell>
                        <TableCell>{row.createdByUserId}</TableCell>
                        <TableCell>{formatDate(row.createdTime)}</TableCell>
                        <TableCell>
                          <TableToolbarMenu>
                            <TableToolbarAction onClick={() => handleEdit(row.id)}>
                              Edit
                            </TableToolbarAction>
                            <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                              Delete
                            </TableToolbarAction>
                          </TableToolbarMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>
      
      {/* Pagination */}
      {filteredRows.length > 0 && !loading && (
        <div style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            totalItems={totalItems}
            page={page}
            pageSize={pageSize}
            pageSizes={[5, 10, 20, 50]}
            onChange={({ page: p, pageSize: ps }) => { 
              setPage(p); 
              setPageSize(ps); 
            }}
          />
        </div>
      )}

      {/* Modal for Add/Edit */}
      <Modal
        open={isModalOpen}
        modalHeading={isEdit ? "Edit Attribute Value" : "Add Attribute Value"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={() => setIsModalOpen(false)}
        onRequestSubmit={handleSave}
      >
        <div style={{ marginBottom: "1rem" }}>
          <TextInput
            id="attribute-value"
            labelText="Attribute Value *"
            value={newAttributeValue}
            onChange={(e) => setNewAttributeValue(e.target.value)}
            placeholder="Enter attribute value"
            required
            invalid={!newAttributeValue.trim()}
            invalidText="Attribute Value is required"
          />
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <ComboBox
          id="attribute-name"
          titleText="Attribute Name *"
          placeholder="Search attribute name..."
          items={specsOptions}
          itemToString={(item) => (item ? item.text : "")}
          selectedItem={newAttributeName}
          onChange={({ selectedItem }) => setNewAttributeName(selectedItem)}
          shouldFilterItem={({ item, inputValue }) => {
          const label = item?.text || "";
          const search = inputValue || "";

          return label.toLowerCase().includes(search.toLowerCase());
           }}
          invalid={!newAttributeName}
          invalidText="Attribute Name is required"
        />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText={deleting ? <InlineLoading description="Deleting..." /> : "Delete"}
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => {
          if (!deleting) setIsDeleteModalOpen(false);
        }}
        onRequestSubmit={handleConfirmDelete}
        primaryButtonDisabled={deleting}
      >
        <p>Are you sure you want to delete this Record.</p>
      </Modal>
    </>
  );
}